import * as React from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import * as S from './styles';
import Provider from './Provider';
import { TC_BUTTON_TYPE } from 'components/TCButton/constants';
import { TCButton } from 'components/TCButton';   
import { ISelectProvidersProps, ProviderInfo, providerSelectorTexts } from './types';
import { Provider as ProviderType, Widgets, ProviderRating} from '../../state/types';
import { getOverallRating } from '../../state/actions';
import { RootState } from 'store/types';
import { cloneDeep } from 'lodash';
import { saveWidgetsConfig } from '../../state/utils';
import FilterCheck from 'components/Filter';

const SelectProviders: React.FC<ISelectProvidersProps> = (props) => {
    const [providersInformation, setProvidersInformation] = React.useState<ProviderInfo[]>([]);
    const [saving, setSaving] = React.useState<boolean>(false);
    const [allowSaving, setAllowSaving] = React.useState<boolean>(false);
    const [selectAll, setSelectAll] = React.useState<boolean|undefined>(undefined);
    const [errorMessage, setErrorMessage] = React.useState<string>('');

    const componentRef = React.useRef(null);
    const dispatch = useDispatch();
    const configWiget = props.widget??'overallRating';

    const {
        widgets,
        providersRating,
        selectedDealerId
    } = useSelector((rootState: RootState) => {
        return {
            widgets: rootState.reviews.widgets,
            providersRating: rootState.reviews.providersRating,
            selectedDealerId: rootState.dealers.dealer.selectedDealer?.id,
        }
    }, shallowEqual);

    const getIsSelected =  (providerSearch: ProviderRating) => {
        if ( props.selectedTab === "entire" && widgets &&
            widgets.entireConfig[configWiget].providers.find((provider: ProviderType) =>  provider.name.toLowerCase()===providerSearch.provider.toLowerCase()))
            return true
        if ( props.selectedTab === "individual" && widgets &&
            widgets.individualConfig[configWiget].providers.find((provider: ProviderType) =>  provider.name.toLowerCase()===providerSearch.provider.toLowerCase()))
            return true
        return false;
    }

    React.useEffect(() => {
        if (providersInformation && props.selectedTab === "entire") {
            props.handleTotalSelected(providersInformation.filter( (provider: ProviderInfo) => provider.selected).length);
        }
    }, [providersInformation]);

    React.useEffect(() => {
        if (widgets && providersRating) {
            let updateWidgetsInformation: ProviderInfo[] = [];
            providersRating.providersRating.forEach((provider: ProviderRating) => {
                updateWidgetsInformation.push({name: provider.provider, selected: getIsSelected(provider), averageStartRating: provider.averageStartRating });
            })
            setProvidersInformation(updateWidgetsInformation);
            validateMinSelectedWidgets(updateWidgetsInformation);
        }
    }, [widgets, providersRating]);

    const handleClickOutside = (e: any) => {
        if (componentRef && componentRef.current) {
            const ref: any = componentRef.current
            if (!ref.contains(e.target)) props.onClose();
        }
    }

    React.useEffect(() => {
        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, [])

    const handleUpdateProviders = (value: boolean, providerName: string, all?: Boolean) => {
        if (widgets) {
            let updateWidgets = cloneDeep(providersInformation);
            if (updateWidgets!==undefined) {
                updateWidgets.forEach ((provider: ProviderType) => {
                    if (all) provider.selected = value;
                    else if ( provider.name===providerName ) provider.selected = value;
                })
                validateMinSelectedWidgets(updateWidgets);
                setProvidersInformation(updateWidgets);   
            }
        }
        if (all===undefined) setSelectAll(undefined);
    }

    const validateMinSelectedWidgets = (newWidgets: ProviderInfo[]) => {
        let totalSelected : number = newWidgets.filter((provider: ProviderInfo) => provider.selected).length;
        if (props.selectedTab === "entire" && totalSelected < 1) setAllowSaving (false);
        else if (props.widget==='providersRating' && totalSelected<3) setAllowSaving (false);
        else if (props.widget==='leaveReview' && (totalSelected<1 || totalSelected>5)) setAllowSaving (false);
        else setAllowSaving (true);

        if (props.widget==='providersRating' && totalSelected>0) {
            let selectedWithReviews : number = newWidgets.filter((provider: ProviderInfo) => provider.selected && provider.averageStartRating===0).length;
            setErrorMessage(selectedWithReviews>0 ? 'You have selected one or more providers that don’t have any reviews currently. They will not be displayed when rendered on the website.' : '');
        } else if (totalSelected===0){
            setErrorMessage(props.selectedTab==='entire' 
                    ? 'You must select at least 1 provider'
                    : 'If you do not select any providers, this section will not appear.');
        }
    } 

    const saveWidgetsInformation = async () => {
        let newProvidersList = providersInformation.filter((provider: ProviderType) => provider.selected)
        let updateWidgets: Widgets|undefined = cloneDeep(widgets);
        if ( props.selectedTab === "entire" && updateWidgets) { 
            Object.keys(updateWidgets.entireConfig).forEach(key => {
                if (updateWidgets?.entireConfig[key].providers)
                    updateWidgets.entireConfig[key].providers = newProvidersList;
            });
        } else if (updateWidgets) {
            updateWidgets.individualConfig[configWiget].providers = newProvidersList;
        }
        if (updateWidgets) {
            try {
                setSaving(true);
                await saveWidgetsConfig(dispatch, updateWidgets);
                if (selectedDealerId && props.widget===undefined) {
                    dispatch(getOverallRating(selectedDealerId, props.selectedTab));
                }
            } catch(e) {
                console.error(e);
            }
        }
        setSaving(false);
        props.onClose();
    }

    const handleSelect = (value: boolean) => {
        handleUpdateProviders(value, '', true);
        setSelectAll(value);
    }

    const top = props.selectedTab === "individual" ? props.posY ? `${props.posY}px`:'0' : '25px';
    const left = props.selectedTab === "individual" && props.posX ? `${props.posX}px` : undefined;
    const hideBars = props.selectedTab === "individual" && props.widget==='leaveReview';

    return (
        <S.MainContainer ref={componentRef} center={props.selectedTab === "individual"} top={top} left={left}>
            <S.Tittle>
                {props.selectedTab==='entire'?
                providerSelectorTexts['entire'].title:
                (providerSelectorTexts[props.widget??'overall'])
                    ?providerSelectorTexts[props.widget??'overall'].title
                    :''
                }
            </S.Tittle>
            {!hideBars &&
            <S.Header>
                <S.SubTittle>
                {props.selectedTab==='entire'?
                providerSelectorTexts['entire'].subtitle:
                (providerSelectorTexts[props.widget??'overall'])
                    ?providerSelectorTexts[props.widget??'overall'].subtitle
                    :''
                }
                </S.SubTittle>
                <S.SubTittle>Ratings Average (stars)</S.SubTittle>
            </S.Header>}
            <S.ProvidersContainer>
                {providersInformation.map((provider: ProviderInfo) => 
                <Provider hideBars={hideBars} label={provider.name} selected={provider.selected} value={provider.averageStartRating} onChange={handleUpdateProviders}/>
                )}
            </S.ProvidersContainer>
            <FilterCheck
                key={`select`}
                label={'Select/Unselect All'}
                type="templateStatus"
                onChange={() => handleSelect(!selectAll)}
                checked={selectAll}
                styleRoot={{padding: '0px 0px', margin: '0 0 9px 3px', height: 'auto'}}
                styledCheckbox={{
                    borderRadius: '5px',
                    border: selectAll ? 'none' : 'solid 2px rgba(189, 189, 189, 0.5)',
                    background: selectAll ?'#2b3a66' : '#fff',
                    width: selectAll ? '22px' : '19.5px',
                    height: selectAll ? '22px' : '19.5px',
                }}
                styleLabel={{
                    fontSize: '14px',
                    color: '#000',
                    marginLeft: '9px'
                }}
                stroke='#fff'
                />
            <S.ErrorMessage>{errorMessage}</S.ErrorMessage>
            <S.Bottom>
                <TCButton
                    type={TC_BUTTON_TYPE.SECONDARY}
                    onClick={() => props.onClose()}
                >
                    Cancel
                </TCButton>
                <TCButton
                    type={TC_BUTTON_TYPE.SECONDARY}
                    onClick={() => saveWidgetsInformation()}
                    disabled={saving || !allowSaving}
                >
                    Save
                </TCButton>
            </S.Bottom>
        </S.MainContainer>
    )
}

export default SelectProviders;